// LDCP-228 — single source of truth for sanitized plain-text input.

/** Allowed punctuation in free-text fields (everything else is blocked). */
export const ALLOWED_SPECIAL_CHARS = ["-", ",", ".", "!", ":", "/", "=", "+", "?"];

export const SAFE_TEXT_MESSAGE =
  `Only letters, numbers, spaces, and ${ALLOWED_SPECIAL_CHARS.join(" ")} are allowed.`;

function escapeForCharClass(chars) {
  return chars.map((c) => c.replace(/[-\\^$*+?.()|[\]{}]/g, "\\$&")).join("");
}

function allowedCharClassContent(specialChars = ALLOWED_SPECIAL_CHARS) {
  return `\\p{L}\\p{N}\\s${escapeForCharClass(specialChars)}`;
}

export function buildSafeTextRegex(specialChars = ALLOWED_SPECIAL_CHARS) {
  return new RegExp(`^[${allowedCharClassContent(specialChars)}]+$`, "u");
}

export const SAFE_TEXT_REGEX = buildSafeTextRegex();

function buildSafeTextFilterRegex(specialChars = ALLOWED_SPECIAL_CHARS) {
  return new RegExp(`[^${allowedCharClassContent(specialChars)}]`, "gu");
}

const SAFE_TEXT_FILTER_REGEX = buildSafeTextFilterRegex();

/** Strip disallowed characters — use in onChange handlers. */
export function filterSafeText(value, specialChars = ALLOWED_SPECIAL_CHARS) {
  const re = specialChars === ALLOWED_SPECIAL_CHARS ? SAFE_TEXT_FILTER_REGEX : buildSafeTextFilterRegex(specialChars);
  return String(value ?? "").replace(re, "");
}

/** Collapse consecutive spaces to one (preserves newlines in multiline values). */
export function collapseRepeatedSpaces(value) {
  return String(value ?? "").replace(/ +/g, " ");
}

/** Safe-text + single spaces; trim edges when `trim` is true (name fields on submit). */
export function normalizeNameText(value, { trim = true, specialChars = ALLOWED_SPECIAL_CHARS } = {}) {
  let s = collapseRepeatedSpaces(filterSafeText(value, specialChars));
  if (trim) s = s.trim();
  return s;
}

/** Live name input — collapse repeated spaces without trimming trailing space while typing. */
export function filterNameText(value, specialChars = ALLOWED_SPECIAL_CHARS) {
  return collapseRepeatedSpaces(filterSafeText(value, specialChars));
}

/** Validate a full string (submit / Yup fallback). */
export function isSafeText(value, { allowEmpty = true } = {}) {
  const v = String(value ?? "");
  if (!v && allowEmpty) return true;
  return SAFE_TEXT_REGEX.test(v);
}
